/**
 * 作品卡 `WorkCard`(卡片体系五类卡之三,2026-07-24 IA 二次收口新建)。
 *
 * ═══ 为什么要建它 ═══════════════════════════════════════════════════════════════
 * 「我做过什么」此前有**四份卡**:海报 `HistoryCard`(PosterDashboardPage 内私有)、
 * 视频 `VideoTaskCard`、分身 `DhTaskCard`、工作台「最近作品」带里第四份手搓 button。
 * 四份的封面比例、时间口径、操作行都各不相同 —— 用户在 `/works` 里把三条线放一起看时,
 * 参差是当场可见的。收成一件后:加第四条线(AI记者)只是往注册表加一行,不再多一份卡。
 *
 * ═══ 规格(照 2026-07-22 提案 §3.3 第 3 类卡)═══════════════════════════════════
 *   封面 + 状态 badge + 场景标签 + 时间 + 归属人;形状锁走 `calc(var(--radius)*1.5)`
 *   (**不写死 px** —— 三皮肤各有自己的 `--radius`,写死会把 comic 压平、standard 撑胖)。
 *
 * ═══ 结构上的两条硬约束 ═══════════════════════════════════════════════════════
 *  1. **卡壳是 `<div>` 不是 `<button>`**:卡内要塞重命名 `<input>` 和多个操作 `<button>`,
 *     button 套 button 是非法 HTML(浏览器会把子节点甩出去重排),且点子按钮必然连带触发外层。
 *     封面与标题各自是真 `<button>`,键盘可达由 `focus-within:ring` 承担。
 *  2. **封面框固定 4:3 + 模糊铺底 + object-contain**:三条线的产物比例天差地别(1:1 主图 /
 *     9:16 竖版视频 / 2160×9727 长图)。裁切会把长图裁成一条,拉伸会变形 —— 只有等比内缩
 *     才能在同一个网格里既整齐又不骗人。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useState, type ReactNode } from "react";
import { AlertTriangle, AudioLines, ImageOff, Lock } from "lucide-react";

import { MediaCardActionRow } from "@/entities/media-asset/ui/MediaCardActionRow";
import { MediaFavoriteToggle } from "@/entities/media-asset/ui/MediaFavoriteToggle";
import { useInlineRename } from "@/entities/media-asset/lib/useInlineRename";
import { relativeTime } from "@/entities/media-asset/lib/relativeTime";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import {
  taskStatusDotClass,
  taskStatusLabel,
  taskStatusSolidBadgeClass,
} from "@/shared/lib/taskStatus";
import { formatQueueStatus } from "@/shared/lib/queueInfo";
import { workOriginLabel } from "../model/cardLabels";
import type { WorkItem } from "../model/types";

/** 可重试的终态(与后端 RETRYABLE 同口径);成功的无需重出。 */
const RETRYABLE = new Set(["FAILED", "BLOCKED", "CANCELLED", "PARTIAL"]);

export interface WorkCardProps {
  item: WorkItem;
  onOpen: () => void;
  /** 不传 = 该线不支持该动作(如新闻线后端无 retry 端点)→ 按钮不渲染,不做假交互。 */
  onRename?: (name: string) => void;
  onToggleFavorite?: () => void;
  onDelete?: () => void;
  onRetry?: () => void;
  deleting?: boolean;
  /** 重试在途:按钮转圈 + 拦连点(重试=新建扣费任务,连点会重复扣)。 */
  retrying?: boolean;
  /** 多选态(海报批量打包下载用);未传 onSelectToggle = 不进多选。 */
  selected?: boolean;
  onSelectToggle?: () => void;
  /**
   * OEM H1:该线模块未开通 / 停用 / 过期 → 卡上挂一行只读提示(看、下载、删照常;新建类动作调用方不传)。
   */
  lockedNotice?: string;
  /** 卡面附加一行(1009:新闻线的「官网」发布状态);不传不渲染。 */
  footnote?: ReactNode;
}

export function WorkCard({
  item,
  onOpen,
  onRename,
  onToggleFavorite,
  onDelete,
  onRetry,
  deleting,
  retrying,
  selected,
  onSelectToggle,
  lockedNotice,
  footnote,
}: WorkCardProps) {
  const rename = useInlineRename({ name: item.title, onRename: onRename ?? (() => {}) });
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = !!item.coverUrl && !coverFailed;
  // 封面左上角:优先场景名(catalog 单源,说得出「这是哪张卡做的」);没打标就退品类
  // (0813:视频片一律没有 sceneKey,直接退部门名等于把 tab 名再抄一遍);两者都没有才退部门名。
  const originLabel = workOriginLabel(item);
  // 1104:PENDING 且后端给了 queueInfo → 「排队中 · 前面 N 单 · 约 X 分钟」;否则原文案不动。
  //   本户上限提示挂 title(角标压在封面上,再加一行会盖住画面)。
  const queue = formatQueueStatus(item.status, item.queueInfo);
  const statusLabel = queue?.label ?? taskStatusLabel(item.status);

  return (
    <div
      data-work-line={item.line}
      data-work-uid={item.uid}
      className={cn(
        "group flex flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS,
        "transition-[transform,box-shadow,border-color] duration-200",
        "hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-lg",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        selected && "border-primary ring-2 ring-primary"
      )}
    >
      <button
        type="button"
        onClick={onSelectToggle ?? onOpen}
        aria-label={onSelectToggle ? `选择 ${item.title}` : `打开 ${item.title}`}
        aria-pressed={onSelectToggle ? !!selected : undefined}
        className="relative block aspect-[4/3] w-full overflow-hidden bg-muted focus-visible:outline-none"
      >
        {showCover ? (
          <>
            {/* 模糊铺底:竖版/长图 contain 后两侧的空白由它填,不留大片死白 */}
            <img
              src={item.coverUrl as string}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
            />
            <img
              src={item.coverUrl as string}
              alt={item.title}
              loading="lazy"
              onError={() => setCoverFailed(true)}
              className="relative z-10 h-full w-full object-contain"
            />
          </>
        ) : item.audio ? (
          // 纯音频产物(分身 tts):本来就没画面,给音频语义占位,不画「无封面」破图
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <AudioLines className="h-6 w-6 opacity-60" />
            <span className="text-2xs">纯音频</span>
          </div>
        ) : (
          // presign 过期 / 未出图 → 语义占位,不留破图
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageOff className="h-5 w-5 opacity-50" />
            <span className="text-2xs">{item.status === "SUCCESS" ? "无封面" : statusLabel}</span>
          </div>
        )}
        <span className="absolute left-2 top-2 z-20 max-w-[70%] truncate rounded-full bg-card/85 px-1.5 py-0.5 text-2xs font-medium text-card-foreground backdrop-blur-sm">
          {originLabel}
        </span>
        <span
          className={cn(
            "absolute bottom-2 left-2 z-20 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-2xs font-medium",
            queue && "max-w-[calc(100%-1rem)]",
            taskStatusSolidBadgeClass()
          )}
          title={queue ? (queue.hint ?? queue.label) : undefined}
        >
          <span
            className={cn("h-1.5 w-1.5 shrink-0 rounded-full", taskStatusDotClass(item.status))}
          />
          {queue ? <span className="truncate">{statusLabel}</span> : statusLabel}
        </span>
      </button>

      <div className="flex flex-1 flex-col gap-1 p-3">
        <div className="flex items-start justify-between gap-2">
          {rename.editing ? (
            <input
              {...rename.inputProps}
              aria-label="重命名作品"
              className="min-w-0 flex-1 rounded border border-input bg-background px-1.5 py-0.5 text-sm font-medium outline-none focus:ring-1 focus:ring-ring"
            />
          ) : (
            <button
              type="button"
              // ★多选态标题也 toggle 选择(codex review P3):否则封面选中、标题却开预览,
              //   同一张卡一半响应多选一半不响应 —— 与封面按钮的 `onSelectToggle ?? onOpen` 对齐。
              onClick={onSelectToggle ?? onOpen}
              // 选择态给显式 aria-label(与封面对齐);非选择态不加,accessible name 用标题文本
              // (避免与封面的「打开 XX」重名,读屏/测试仍能各自唯一定位)。
              aria-label={onSelectToggle ? `选择 ${item.title}` : undefined}
              aria-pressed={onSelectToggle ? !!selected : undefined}
              title={item.title}
              className="min-w-0 flex-1 text-left text-sm font-medium focus-visible:outline-none"
            >
              <span className="block truncate">{item.title}</span>
            </button>
          )}
          {onToggleFavorite && (
            <MediaFavoriteToggle isFavorite={item.isFavorite} onToggle={onToggleFavorite} />
          )}
        </div>

        {item.meta && (
          <div className="truncate font-mono text-2xs text-muted-foreground">{item.meta}</div>
        )}

        {/* 左侧部门+归属人 truncate、右侧时间 shrink-0:窄卡下省略号收敛,不逐字换行 */}
        <div className="flex items-center justify-between gap-2 text-2xs text-muted-foreground">
          <span className="min-w-0 truncate">
            {item.lineLabel}
            {item.ownerName ? ` · ${item.ownerName}` : ""}
          </span>
          <span className="shrink-0 whitespace-nowrap">{relativeTime(item.sortAt)}</span>
        </div>

        {footnote}

        {item.errorMessage && (
          <div className="mt-0.5 flex items-start gap-1 rounded-[calc(var(--radius)*0.75)] border border-destructive/30 bg-destructive/10 px-2 py-1 text-2xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{maskVendor(item.errorMessage)}</span>
          </div>
        )}

        {lockedNotice && (
          <div
            className="mt-0.5 flex items-start gap-1 text-2xs text-muted-foreground"
            data-testid="work-card-locked"
            title={lockedNotice}
          >
            <Lock className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-2">{lockedNotice}</span>
          </div>
        )}

        {/* ★任一动作在就渲染操作行,行内按钮各自「不传就不出」(MediaCardActionRow 同口径)。
            此前要求 rename+delete **都在**才出整行 —— 新闻线故意不实现改名(后端无端点),
            结果把它唯一有的删除也陪葬掉了(E2E 0810 RP-WK-03)。
            「最近 6 条」这种只读位仍然一个 handler 都不传,整行照旧不出现。 */}
        {(onRename || onDelete || onRetry) && (
          <MediaCardActionRow
            onRename={onRename ? rename.begin : undefined}
            onDelete={onDelete}
            onRetry={onRetry}
            canRetry={!!onRetry && RETRYABLE.has(item.status)}
            deleting={deleting}
            retrying={retrying}
            className="mt-auto"
          />
        )}
      </div>
    </div>
  );
}
